import { FC, Key, useEffect, useMemo, useRef, useState, } from "react";
// 组件
import SuperSelect from '../SuperSelect';
import MoreModalTable from "./subpage/moreModalTable";
// 方法
import { formarValue } from "./util";
import { arrayEqual } from "../utils/dataUtils";
// 常量
import { ModalTableActionType } from "../constants/interface";
import { API_STATUS_ENUM } from "../constants/enum";
import { SUCCESS_STATUS_CODE } from "../constants/constant";
import { DEFAULT_FIELD_NAMES, FieldNamesType, SelectOption, SelectValue, SuperSelectActionType } from "../SuperSelect/interface";
import { SuperSelectTableProps } from "./interface";

/** 异步下拉列表 */
const SuperSelectTable: FC<SuperSelectTableProps> = props => {
  const {
    labelKey, valueKey, subLabelKey, value: outValue, childrenKey, childrenLabelKey, childrenValueKey, copy, routeLinkProps,
    childrenSubLabelKey, fieldNames, searchKey, dataKey, selectTableKey, otherParams, isTableList, placeholder, showMore, moreText,
    columns, allowClear, showSearch, isLocalSearch, defaultSelectedFirstItem, mode, initOptions, disabled, style, className,
    formItems, moreTableShowTab, associateSelectNames, groupFields, moreModalSize, searchPlaceholder, disabledBorderDashed,
    dropdownMatchSelectWidth, initialLoading, disabledRender, isOpenUpdate, isDisabledEllipsis, onRenderLabel,
    fetchApi, filterData, beforeFetch, onSuccess, onChange, getPopupContainer, renderOption, onBlur, onFocus,
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<SelectOption[]>();
  const [value, setValue] = useState<SelectValue>();
  const superSelectRef = useRef<SuperSelectActionType>();
  const apiStatus = useRef<API_STATUS_ENUM>(API_STATUS_ENUM.NOT_START); // 接口状态
  const fetchNumberRef = useRef<number>(0); // 当前请求标识
  const moreModalRef = useRef<ModalTableActionType>(); // 更多列表弹窗
  const selectedOptions = useRef<SelectOption | SelectOption[]>(); // 选中数据
  const prevMoreAddOptions = useRef<SelectOption | SelectOption[]>(); // 上一次从选择更多中选中的不在选项中的数据
  const timer = useRef<any>(); // 延时过滤选项定时器
  const isChangeOtherParams = useRef<boolean>(false);
  const otherParamsString = otherParams ? JSON.stringify(otherParams) : '';
  const prevBeforeFetchParams = useRef<any>(); // 上一次请求的参数
  const initOptionsStringify = JSON.stringify(initOptions || []);
  useEffect(() => {
    if (initialLoading || (fieldNames?.defauleStatus && !outValue)) {
      handleFocus();
    }
  }, []);
  useEffect(() => {
    const outValueType: boolean = Array.isArray(outValue);
    if (mode && !outValueType) {
      setValue(undefined);
      return;
    }
    if (!mode && outValueType) {
      setValue(undefined);
      return;
    }
    setValue(outValue);
  }, [outValue]);
  useEffect(() => {
    if (Array.isArray(initOptions) && initOptions.length) {
      if (apiStatus.current !== API_STATUS_ENUM.SUCCESS) {
        setOptions(initOptions);
      } else {
        const newOptions = Array.isArray(options) ? [...options] : [];
        initOptions.forEach(item => {
          const key = valueKey || fieldNames?.value || DEFAULT_FIELD_NAMES.label;
          const findItem = newOptions?.find(v => v[key] === item[key]);
          if (!findItem) {
            newOptions?.unshift(item);
          }
        });
        setOptions(newOptions);
      }
    }
  }, [initOptionsStringify]);
  useEffect(() => {
    if (isChangeOtherParams.current && apiStatus.current !== API_STATUS_ENUM.NOT_START) {
      selectedOptions.current = undefined;
      apiStatus.current = API_STATUS_ENUM.NOT_START;
      superSelectRef.current?.emptySearch();
      setValue(undefined);
      if (initialLoading || fieldNames?.defauleStatus) {
        handleFocus();
        return;
      }
      onChange?.(undefined);
    }
    isChangeOtherParams.current = true;
  }, [otherParamsString]);
  /** 下拉列表获取焦点 */
  const handleFocus = async () => {
    if (!fetchApi) return;
    if (isOpenUpdate || apiStatus.current === API_STATUS_ENUM.NOT_START) {
      await getOptions();
    }
    onFocus?.();
  };
  /** 搜索 */
  const handleSearch = async (searchValue?: string) => {
    if (isLocalSearch) return;
    timer.current && clearTimeout(timer.current);
    loading && clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      getOptions(searchValue, true);
    }, 300);
  }
  /** value值改变 */
  const handleChange = (val?: SelectValue, options?: SelectOption | SelectOption[]) => {
    selectedOptions.current = options;
    setValue(val);
    onChange?.(val, options);
  };
  /** 远程获取下拉选项 */
  const getOptions = async (searchValue?: string, isSearch?: boolean) => {
    let newParams: any = { ...otherParams };
    const searchName = searchKey || labelKey || fieldNames?.label || DEFAULT_FIELD_NAMES.label;
    if (searchValue && searchName) {
      newParams[searchName] = searchValue;
    }
    if (beforeFetch) {
      newParams = beforeFetch(newParams);
      if (newParams === false) {
        apiStatus.current = API_STATUS_ENUM.ERROR;
        setOptions(undefined);
        return;
      }
    }
    if (isTableList) {
      newParams = {
        page: 1,
        size: 20,
        ...newParams,
      };
    }
    // 是否需要从缓存中读取
    if (selectTableKey) {
      const CACHE_OPTIONS = window.sessionStorage.getItem(selectTableKey);
      if (CACHE_OPTIONS) {
        if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
          timer.current && clearInterval(timer.current);
          timer.current = setInterval(() => {
            const resData = window.sessionStorage.getItem(selectTableKey);
            if (resData && resData !== API_STATUS_ENUM.LOADING) {
              clearInterval(timer.current);
              const data = JSON.parse(resData);
              handleDataProcessing(data, searchValue);
            }
          }, 100);
        } else {
          const data = JSON.parse(CACHE_OPTIONS);
          handleDataProcessing(data, searchValue);
        }
        return;
      } else {
        window.sessionStorage.setItem(selectTableKey, API_STATUS_ENUM.LOADING);
      }
    }
    apiStatus.current = API_STATUS_ENUM.LOADING;
    const currentNumebr = fetchNumberRef.current + 1;
    fetchNumberRef.current = currentNumebr;
    // 比对两次请求参数是否相同，相同这不在发起请求
    if (!isOpenUpdate) {
      if (prevBeforeFetchParams.current) {
        const prevStringify = JSON.stringify(prevBeforeFetchParams.current);
        const nextStringify = JSON.stringify(newParams);
        if (prevStringify === nextStringify) return;
        prevBeforeFetchParams.current = newParams;
        if (!isSearch) selectedOptions.current = undefined;
      } else {
        prevBeforeFetchParams.current = newParams;
      }
    }
    setLoading(true);
    const res = await fetchApi(newParams);
    setLoading(false);
    if (res.code === SUCCESS_STATUS_CODE && currentNumebr === fetchNumberRef.current) {
      apiStatus.current = API_STATUS_ENUM.SUCCESS;
      const data = (dataKey ? res.data?.[dataKey] : res.data) || [];
      // 数据存入缓存
      if (selectTableKey) {
        window.sessionStorage.setItem(
          selectTableKey,
          JSON.stringify(data || []),
        );
      }
      handleDataProcessing(data, searchValue);
      return;
    }
    apiStatus.current = API_STATUS_ENUM.ERROR;
  };
  /** 远程获取的数据处理 */
  const handleDataProcessing = (data: any = [], searchValue?: string) => {
    const result: any[] = filterData ? filterData(data) : data;
    const selectedRows = selectedOptions.current;
    const currentValueKey = valueKey || fieldNames?.value || DEFAULT_FIELD_NAMES.value;
    let otherOptions: SelectOption[] = [];
    if (selectedRows) {
      if (Array.isArray(selectedRows)) {
        otherOptions.push(...selectedRows);
      } else {
        otherOptions.push(selectedRows);
      }
    }
    if (value && initOptions) otherOptions.push(...initOptions);
    if (otherOptions.length) {
      otherOptions.forEach(item => {
        const findOption = result.find(v => v[currentValueKey] === item[currentValueKey]);
        if (!findOption) {
          result.unshift(item);
        }
      });
    }
    setOptions(result);
    if (Array.isArray(result) && result.length && !value && defaultSelectedFirstItem) {
      const firstValue = result[0][currentValueKey];
      if (mode === 'multiple') {
        setValue([firstValue]);
        onChange?.([firstValue], [result[0]]);
      } else {
        setValue(firstValue);
        onChange?.(firstValue, result[0]);
      }
      onSuccess?.(result);
      return;
    }
    if (Array.isArray(result) && result.length && fieldNames?.defauleStatus && !value) {
      const defauleValue = result.find(item => item[fieldNames.defauleStatus as string])?.[currentValueKey];
      if (mode === 'multiple') {
        setValue([defauleValue]);
        onChange?.([defauleValue], [result[0]]);
      } else {
        setValue(defauleValue);
        onChange?.(defauleValue, result[0]);
      }
      onSuccess?.(result);
      return;
    }
    onSuccess?.(result);
  };
  /** 点击更多 */
  const handleClickMore = () => {
    superSelectRef.current?.blur();
    moreModalRef.current?.show();
  };
  /** 点击确定 */
  const handleOk = (selectedRowKeys?: Key[], selectedRows?: any[]) => {
    const newValue: SelectValue | undefined = mode === 'multiple' ? selectedRowKeys : selectedRowKeys?.[0];
    const currentValueKey = valueKey || fieldNames?.value || DEFAULT_FIELD_NAMES.value;
    // @ts-ignore
    if (arrayEqual(value, newValue)) {
      return;
    }
    const nextOptions: SelectOption[] = [];
    const addOptions: SelectOption[] = [];
    // 移除上一次从选择更多中添加过来的数据
    options?.forEach(item => {
      const prevMoveData = prevMoreAddOptions.current;
      if (prevMoveData) {
        if (Array.isArray(prevMoveData)) {
          const findItem = prevMoveData.find(v => v[currentValueKey] === item[currentValueKey]);
          if (!findItem) nextOptions.push(item);
        } else {
          const findItem = prevMoveData[currentValueKey] === item[currentValueKey];
          if (!findItem) nextOptions.push(item);
        }
        return;
      }
      nextOptions.push(item);
    });
    if (mode === 'multiple') {
      selectedOptions.current = selectedRows;
      selectedRows?.forEach(item => {
        const findOption = nextOptions.find(v => v[currentValueKey] === item[currentValueKey]);
        if (!findOption) {
          addOptions.push(item);
        }
      });
      prevMoreAddOptions.current = addOptions;
      onChange?.(newValue, selectedRows);
    } else {
      const newOption = selectedRows?.[0];
      selectedOptions.current = newOption;
      const findOption = nextOptions.find(item => item[currentValueKey] === newValue);
      if (!findOption) {
        prevMoreAddOptions.current = newOption;
        addOptions.push(newOption);
      }
      onChange?.(newValue, newOption);
    }
    nextOptions.unshift(...addOptions);
    setOptions(nextOptions);
    setValue(newValue);
  };
  /** 字段映射 */
  const newFieldNames: FieldNamesType = useMemo(() => {
    return {
      label: labelKey || DEFAULT_FIELD_NAMES.label,
      value: valueKey || DEFAULT_FIELD_NAMES.value,
      subLabel: subLabelKey || DEFAULT_FIELD_NAMES.subLabel,
      children: childrenKey || DEFAULT_FIELD_NAMES.children,
      childrenLabel: childrenLabelKey || DEFAULT_FIELD_NAMES.childrenLabel,
      childrenSubLabel: childrenSubLabelKey || DEFAULT_FIELD_NAMES.childrenSubLabel,
      childrenValue: childrenValueKey || DEFAULT_FIELD_NAMES.childrenValue,
      ...fieldNames,
    };
  }, [labelKey, valueKey, subLabelKey, childrenKey, childrenLabelKey, childrenValueKey, childrenSubLabelKey, fieldNames,]);

  return (
    <>
      <SuperSelect
        style={style}
        className={className}
        actionRef={superSelectRef}
        loading={loading}
        options={options}
        showSearch={showSearch}
        value={value}
        placeholder={placeholder}
        fieldNames={newFieldNames}
        allowClear={allowClear}
        isLocalSearch={isLocalSearch}
        searchPlaceholder={searchPlaceholder}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        disabledBorderDashed={disabledBorderDashed}
        mode={mode}
        copy={copy}
        routeLinkProps={routeLinkProps}
        isDisabledEllipsis={isDisabledEllipsis}
        disabledRender={disabledRender}
        disabled={disabled}
        dropdownFooterRender={
          showMore ? (
            <div className="link-text" onClick={handleClickMore}>{moreText}</div>
          ) : undefined
        }
        renderOption={renderOption}
        onFocus={handleFocus}
        onBlur={onBlur}
        onSearch={handleSearch}
        onChange={handleChange}
        onRenderLabel={onRenderLabel}
      />
      {
        showMore && (
          <MoreModalTable
            actionRef={moreModalRef}
            fetchApi={fetchApi}
            rowKey={valueKey || fieldNames?.value || DEFAULT_FIELD_NAMES.value}
            columns={columns}
            mode={mode}
            formItems={formItems}
            otherParams={otherParams}
            rowSelectionType={mode === 'multiple' ? 'checkbox' : 'radio'}
            moreTableShowTab={moreTableShowTab}
            labelKey={labelKey || fieldNames?.label || DEFAULT_FIELD_NAMES.label}
            associateSelectNames={associateSelectNames}
            groupFields={groupFields}
            moreModalSize={moreModalSize}
            selectedInfo={{ selectedRowKeys: formarValue(value), selectedRows: formarValue(selectedOptions.current) }}
            onOk={handleOk}
            getPopupContainer={getPopupContainer}
            beforeFetch={beforeFetch}
          />
        )
      }
    </>
  );
};

SuperSelectTable.defaultProps = {
  dataKey: 'list',
  isTableList: true,
  placeholder: '请选择',
  showMore: true,
  moreText: '选择更多',
  allowClear: true,
  moreTableShowTab: true,
  moreModalSize: 'middle',
  isLocalSearch: false,
  defaultSelectedFirstItem: false,
  isOpenUpdate: false,
  dropdownMatchSelectWidth: 300,
};

export default SuperSelectTable;
